/**
 * Admin Dashboard Component
 *
 * Displays analytics and monitoring data for the legal n8n-based chat system.
 * Shows summary statistics and a paginated list of interactions.
 *
 * Protected route: Only accessible to authenticated users
 * Route: /admin
 */

import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { API_ENDPOINTS, buildApiUrl } from '../../config/apiConfig';
import AdminAnalyticsSection from '../../components/AdminAnalyticsSection';
import IngestionAnalyticsSection from '../../components/IngestionAnalyticsSection';
import LegalIntelligenceAnalyticsSection from '../../components/LegalIntelligenceAnalyticsSection';
import '../AdminDashboard.css';

const AdminLegacyOverviewPage = () => {
  const { t, isRTL } = useLanguage();
  const [summary, setSummary] = useState(null);
  const [interactions, setInteractions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize] = useState(20);
  const [selectedInteraction, setSelectedInteraction] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const fetchSummary = async () => {
    try {
      const response = await fetch(buildApiUrl(API_ENDPOINTS.ADMIN_SUMMARY), {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setSummary(data);
      }
    } catch (error) {
      console.error('Error fetching summary:', error);
    }
  };

  const fetchInteractions = async (pageNum = 1) => {
    try {
      const response = await fetch(
        buildApiUrl(`${API_ENDPOINTS.ADMIN_INTERACTIONS}?page=${pageNum}&pageSize=${pageSize}`),
        { credentials: 'include' }
      );
      if (response.ok) {
        const data = await response.json();
        setInteractions(data.interactions);
        setTotalPages(data.totalPages);
        setPage(data.page);
      }
    } catch (error) {
      console.error('Error fetching interactions:', error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchSummary(), fetchInteractions(page)]);
      setLoading(false);
    };
    loadData();
  }, [page]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const shortenId = (id) => {
    if (!id) return 'N/A';
    return id.substring(0, 8) + '...';
  };

  const extractSummary = (fullAnswer) => {
    if (!fullAnswer) return 'N/A';
    const detailMarker = '📖 الشرح التفصيلي';
    const markerIndex = fullAnswer.indexOf(detailMarker);
    if (markerIndex > 0) {
      return fullAnswer.substring(0, markerIndex).trim();
    }
    const paragraphs = fullAnswer.split(/\n\n+/);
    if (paragraphs.length > 1) {
      return paragraphs[0].trim();
    }
    if (fullAnswer.length > 150) {
      const sentenceEnd = fullAnswer.substring(0, 150).lastIndexOf('.');
      if (sentenceEnd > 50) {
        return fullAnswer.substring(0, sentenceEnd + 1);
      }
      return fullAnswer.substring(0, 150).trim() + '...';
    }
    return fullAnswer;
  };

  const openModal = (interaction) => {
    setSelectedInteraction(interaction);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedInteraction(null);
  };

  const renderInteractionRow = (interaction) => (
    <tr key={interaction.id}>
      <td>{formatDate(interaction.created_at)}</td>
      <td className="monospace">{shortenId(interaction.session_id)}</td>
      <td>{interaction.user_id || t('adminPage.guest')}</td>
      <td>
        <span className={`source-badge source-${interaction.source_page}`}>
          {interaction.source_page}
        </span>
      </td>
      <td className="text-cell clickable" onClick={() => openModal(interaction)}>
        {interaction.question_text && interaction.question_text.length > 60
          ? interaction.question_text.substring(0, 60) + '...'
          : interaction.question_text || 'N/A'}
      </td>
      <td className="text-cell clickable" onClick={() => openModal(interaction)}>
        {extractSummary(interaction.answer_text)}
      </td>
    </tr>
  );

  const topInteractions = interactions.slice(0, 5);
  const restInteractions = interactions.slice(5);

  if (loading) {
    return (
      <div className={`admin-dashboard ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
        <div className="admin-initial-loader">
          <div className="admin-state admin-state--loading" aria-live="polite">
            <div className="admin-loading-dots" aria-hidden>
              <span />
              <span />
              <span />
            </div>
            <p className="admin-state__message">{t('adminPage.loading')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`admin-dashboard ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
      <header className="admin-hero">
        <h1 className="admin-hero__title">{t('adminPage.title')}</h1>
        <p className="admin-hero__subtitle">{t('adminPage.subtitle')}</p>
      </header>

      <AdminAnalyticsSection />

      <IngestionAnalyticsSection />

      <LegalIntelligenceAnalyticsSection />

      <section className="admin-section-card admin-section-platform" aria-labelledby="admin-platform-heading">
        <div className="admin-section-header">
          <h2 id="admin-platform-heading" className="admin-section-title">
            {t('adminPage.platformTitle')}
          </h2>
          <p className="admin-section-desc">{t('adminPage.platformDesc')}</p>
        </div>
        <div className="admin-summary">
          <div className="summary-card">
            <div className="summary-label">{t('adminPage.metricTotalUsers')}</div>
            <div className="summary-value">{summary?.totalUsers || 0}</div>
          </div>
          <div className="summary-card">
            <div className="summary-label">{t('adminPage.metricRegistered')}</div>
            <div className="summary-value">{summary?.totalRegisteredUsers || 0}</div>
          </div>
          <div className="summary-card">
            <div className="summary-label">{t('adminPage.metricVisitors')}</div>
            <div className="summary-value">{summary?.totalVisitors || 0}</div>
          </div>
          <div className="summary-card">
            <div className="summary-label">{t('adminPage.metricVisits')}</div>
            <div className="summary-value">{summary?.totalVisits || 0}</div>
          </div>
          <div className="summary-card">
            <div className="summary-label">{t('adminPage.metricInteractions')}</div>
            <div className="summary-value">{summary?.totalInteractions || 0}</div>
          </div>
          <div className="summary-card">
            <div className="summary-label">{t('adminPage.metricToday')}</div>
            <div className="summary-value">{summary?.interactionsToday || 0}</div>
          </div>
          <div className="summary-card">
            <div className="summary-label">{t('adminPage.metric24h')}</div>
            <div className="summary-value">{summary?.last24HoursInteractions || 0}</div>
          </div>
        </div>
      </section>

      <section
        className="admin-section-card admin-section-interactions"
        aria-labelledby="admin-recent-interactions-heading"
      >
        <div className="admin-section-header">
          <h2 id="admin-recent-interactions-heading" className="admin-section-title">
            {t('adminPage.interactionsTitle')}
          </h2>
          <p className="admin-section-desc">{t('adminPage.interactionsDesc')}</p>
        </div>

        {interactions.length === 0 ? (
          <div className="admin-empty-state" role="status">
            <p className="admin-empty-state__text">{t('adminPage.emptyInteractions')}</p>
          </div>
        ) : (
          <>
            <div className="admin-table-surface">
              <div className="interactions-table-container">
                <table className="interactions-table" role="table">
                  <colgroup>
                    <col className="interactions-col interactions-col--date" />
                    <col className="interactions-col interactions-col--session" />
                    <col className="interactions-col interactions-col--user" />
                    <col className="interactions-col interactions-col--source" />
                    <col className="interactions-col interactions-col--q" />
                    <col className="interactions-col interactions-col--a" />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>{t('adminPage.colDate')}</th>
                      <th>{t('adminPage.colSession')}</th>
                      <th>{t('adminPage.colUser')}</th>
                      <th>{t('adminPage.colSource')}</th>
                      <th>{t('adminPage.colQuestion')}</th>
                      <th>{t('adminPage.colAnswer')}</th>
                    </tr>
                  </thead>
                  <tbody>{topInteractions.map((interaction) => renderInteractionRow(interaction))}</tbody>
                </table>
              </div>
              {restInteractions.length > 0 && (
                <div
                  className="recent-events-scroll"
                  role="region"
                  aria-label={t('adminPage.scrollRegion')}
                  tabIndex={0}
                >
                  <table className="interactions-table interactions-table--cont">
                    <colgroup>
                      <col className="interactions-col interactions-col--date" />
                      <col className="interactions-col interactions-col--session" />
                      <col className="interactions-col interactions-col--user" />
                      <col className="interactions-col interactions-col--source" />
                      <col className="interactions-col interactions-col--q" />
                      <col className="interactions-col interactions-col--a" />
                    </colgroup>
                    <tbody>{restInteractions.map((interaction) => renderInteractionRow(interaction))}</tbody>
                  </table>
                </div>
              )}
            </div>
            {totalPages > 1 && (
              <div className="pagination">
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="pagination-btn"
                >
                  {t('adminPage.prev')}
                </button>
                <span className="pagination-info">
                  {t('adminPage.pageInfo', { current: page, total: totalPages })}
                </span>
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="pagination-btn"
                >
                  {t('adminPage.next')}
                </button>
              </div>
            )}
          </>
        )}
      </section>

      {showModal && selectedInteraction && (
        <div className="modal-overlay" onClick={closeModal} role="dialog" aria-modal="true" aria-labelledby="admin-modal-title">
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 id="admin-modal-title">{t('adminPage.modalTitle')}</h2>
              <button type="button" className="modal-close" onClick={closeModal} aria-label={t('adminPage.close')}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="detail-section">
                <label>{t('adminPage.labelDate')}</label>
                <p>{formatDate(selectedInteraction.created_at)}</p>
              </div>
              <div className="detail-section">
                <label>{t('adminPage.labelSession')}</label>
                <p className="monospace">{selectedInteraction.session_id}</p>
              </div>
              <div className="detail-section">
                <label>{t('adminPage.labelUser')}</label>
                <p>{selectedInteraction.user_id || t('adminPage.guest')}</p>
              </div>
              <div className="detail-section">
                <label>{t('adminPage.labelSource')}</label>
                <p>
                  <span className={`source-badge source-${selectedInteraction.source_page}`}>
                    {selectedInteraction.source_page}
                  </span>
                </p>
              </div>
              <div className="detail-section">
                <label>{t('adminPage.labelQuestion')}</label>
                <p className="full-text">{selectedInteraction.question_text}</p>
              </div>
              <div className="detail-section">
                <label>{t('adminPage.labelAnswer')}</label>
                <p className="full-text" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {selectedInteraction.answer_text || 'N/A'}
                </p>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="modal-btn-close" onClick={closeModal}>
                {t('adminPage.close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminLegacyOverviewPage;
